import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, Mic } from "lucide-react";
import { JitsiFrame } from "@/components/JitsiFrame";
import { LangSwitcher } from "@/components/LangSwitcher";
import { LegalLinks } from "@/components/LegalLinks";
import { Wordmark } from "@/components/Nav";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { api } from "@/lib/api";

export default function GuestJoin() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { config } = useAuth();
  const { t } = useLang();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);
  const [name, setName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [joined, setJoined] = useState(false);
  const rejoinRef = useRef(false);

  useEffect(() => {
    api.get(`/public/meetings/${id}`, { params: { code: params.get("code") || "" } }).then((r) => setInfo(r.data)).catch(() => setError(true));
  }, [id, params]);

  const join = (e) => {
    e.preventDefault();
    if (!name.trim() || !agreed) return;
    if (!rejoinRef.current) {
      rejoinRef.current = true;
      api.post(`/public/meetings/${id}/consent`, { code: params.get("code") || "", name: name.trim() }).catch(() => {});
    }
    setJoined(true);
  };

  if (joined && info) {
    return (
      <div className="flex h-screen flex-col bg-vox-deep" data-testid="guest-room-page">
        <header className="flex h-16 shrink-0 items-center justify-between border-b border-vox-line bg-vox-bg px-6">
          <Wordmark brand={config.brand} to="/" />
          <span className="truncate text-sm text-vox-muted">{info.title}</span>
        </header>
        <div className="relative flex-1">
          <JitsiFrame domain={info.domain} roomName={info.room_name} displayName={name} subject={info.title} onLeave={() => setJoined(false)} />
        </div>
      </div>
    );
  }

  return (
    <div className="grain relative flex min-h-screen flex-col" data-testid="guest-join-page">
      <header className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-6">
        <Wordmark brand={config.brand} to="/" />
        <LangSwitcher compact />
      </header>
      <main className="relative z-10 grid flex-1 place-items-center px-6 pb-16">
        <div className="panel reveal w-full max-w-md p-8">
          <div className="mono-label mb-3">{t("room.guest")}</div>
          {error ? (
            <p className="text-sm text-[#ff9b9e]" data-testid="guest-invalid-link">{t("room.invalidLink")}</p>
          ) : !info ? (
            <div className="flex items-center gap-2 text-sm text-vox-dim"><Loader2 size={14} className="animate-spin" />{t("common.loading")}</div>
          ) : (
            <form onSubmit={join} className="space-y-4">
              <h1 className="text-2xl font-semibold text-vox-text" data-testid="guest-meeting-title">{info.title}</h1>
              <div>
                <label className="mono-label mb-2 block" htmlFor="guest-name">{t("room.enterName")}</label>
                <input id="guest-name" required className="input" value={name} onChange={(e) => setName(e.target.value)} data-testid="guest-name-input" />
              </div>
              <div className="rounded-xl border border-vox-amber/30 bg-vox-amber/5 p-4" data-testid="guest-recording-notice">
                <div className="flex items-start gap-2 text-xs leading-relaxed text-vox-muted"><Mic size={14} className="mt-0.5 shrink-0 text-vox-amber" />{t("room.guestNotice")}</div>
                <label className="mt-3 flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-vox-muted">
                  <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-[#94BFC7]" data-testid="guest-consent-checkbox" />
                  <span>{t("room.guestConsent")} <LegalLinks /></span>
                </label>
              </div>
              <button type="submit" disabled={!agreed || !name.trim()} className="btn-primary w-full disabled:opacity-50" data-testid="guest-join-button">{t("room.joinBtn")}</button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
